import { Trans } from 'react-i18next';
import { TriangleAlert } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import { formatCdmxDay } from '@subscriptions/presentation/utils';

/** Visual treatment: a warning banner in the block detail, plain copy inside the wizard card. */
type ReactivationClabeNoticeVariant = 'banner' | 'text';

const VARIANT_CLASSES: Record<ReactivationClabeNoticeVariant, string> = {
  banner: 'flex items-start gap-2 rounded-lg border border-warning bg-bg-warning p-3 text-warning',
  text: 'text-sm text-foreground',
};

/** Maps the `<strong>` tag in the i18n copy to a semibold span. */
const strongComponents = { strong: <strong className="font-semibold" /> };

interface ReactivationClabeNoticeProps {
  className?: string;
  /** Last day the CLABE works (`YYYY-MM-DD`, CDMX). */
  clabeValidUntil: string;
  /** Day the amount was quoted (`YYYY-MM-DD`, CDMX). */
  quotedOn: string;
  variant?: ReactivationClabeNoticeVariant;
}

/**
 * The two validity dates of a reactivation CLABE, in the wording of CA-13.
 *
 * The amount holds for the day it was quoted and the CLABE until the cycle closes, so the copy
 * switches to its last-day variant when both dates land on the same day. Saying it wrong is the
 * most expensive failure of the flow: reconciliation has no tolerance, and a deposit for
 * yesterday's amount is returned instead of credited.
 *
 * @param {ReactivationClabeNoticeProps} props - Component properties
 * @returns {React.ReactElement} Validity notice
 */
export const ReactivationClabeNotice = ({
  className,
  clabeValidUntil,
  quotedOn,
  variant = 'banner',
}: ReactivationClabeNoticeProps) => {
  const isLastClabeDay = quotedOn === clabeValidUntil;

  const copy = (
    <Trans
      components={strongComponents}
      i18nKey={isLastClabeDay ? 'subscriptions:reactivation.clabeLastDay' : 'subscriptions:reactivation.clabeValidity'}
      values={{ today: formatCdmxDay(quotedOn), validUntil: formatCdmxDay(clabeValidUntil) }}
    />
  );

  if (variant === 'text') {
    return <p className={cn(VARIANT_CLASSES.text, className)}>{copy}</p>;
  }

  return (
    <div className={cn(VARIANT_CLASSES.banner, className)}>
      <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="text-sm">{copy}</p>
    </div>
  );
};
